import { FC, ReactElement, ReactNode, useCallback, useRef } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { theme } from '@frontend/theme';
import { Button, ButtonProps } from '@frontend/design-system';

export interface StickyPromotionWrapperProps {
  children: ReactNode | ((config: { isPromoVisible: boolean }) => ReactElement);
  promoComponent: ReactElement | null;
  scrollButtonProps?: ButtonProps;
  showPromo?: boolean;
  styles?: Interpolation<Theme>;
}

export const StickyPromotionWrapper: FC<StickyPromotionWrapperProps> = ({
  children,
  promoComponent,
  scrollButtonProps,
  showPromo = true,
  styles,
}) => {
  const { ref: childInViewRef, inView } = useInView();
  const childRef = useRef<HTMLDivElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLDivElement) => {
      childRef.current = node;
      childInViewRef(node);
    },
    [childInViewRef]
  );

  const scrollToChild = () => {
    childRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  if (!promoComponent || !showPromo) {
    return typeof children === 'function' ? children({ isPromoVisible: false }) : children;
  }

  return (
    <>
      <div css={[stickyHighlightStyles, styles, !inView && stickyBgBlurStyles]}>
        {promoComponent}
        {!inView && (
          <Button
            iconName='down-small'
            variant='secondary'
            size='small'
            css={arrowButtonStyles}
            {...scrollButtonProps}
            onClick={(e) => {
              scrollToChild();
              scrollButtonProps?.onClick?.(e);
            }}
          />
        )}
      </div>
      <div ref={setRefs} css={[{ marginTop: theme.spacing(2) }]}>
        {typeof children === 'function' ? children({ isPromoVisible: inView }) : children}
      </div>
    </>
  );
};

const stickyHighlightStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  alignItems: 'center',
  position: 'sticky',
  bottom: 0,
});

const stickyBgBlurStyles = css({
  ':before': {
    content: '""',
    position: 'absolute',
    display: 'block',
    zIndex: -1,
    width: `calc(100% + ${theme.spacing(2)})`,
    height: `calc(100% + ${theme.spacing(4)})`,
    top: `-${theme.spacing(1)}`,
    left: `-${theme.spacing(1)}`,
    backdropFilter: 'blur(4px)',
    background: 'linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.5) 100%)',
  },
});

const arrowButtonStyles = css({
  boxShadow: theme.shadows.heavy,
  backgroundColor: theme.colors.white,
  animation: 'bounce 1s infinite',
  ':hover': {
    animation: 'none',
    transform: 'translateY(0)',
  },
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-4px)',
    },
  },
});
